import { useRef, useState } from 'react'
import { Camera, ImageOff } from 'lucide-react'

import { cn } from '@/lib/utils'
import {
  applyCropImage,
  moveCropRectangle,
  normalizeCropRectangle,
} from '@/lib/crop-image'
import type { CropPoint, CropRectangle } from '@/lib/crop-image'

/** Ekran görüntüsünün maskelenmediği uyarısı; hem bildirim formunda hem sohbette aynen gösterilir. */
export const SCREENSHOT_NOT_MASKED_COPY =
  'Ekran görüntüsü otomatik maskelenmez. Göndermeden önce kontrol et; istersen kırpabilir veya çıkarabilirsin.'

const CROP_FAILURE_COPY =
  'Ekran görüntüsü kırpılamadı. Orijinal görüntü korundu; yeniden deneyebilir veya kırpmadan devam edebilirsin.'

export type ScreenshotEditorProps = {
  source: string
  originalSource: string
  included: boolean
  isSubmitting: boolean
  isCropped: boolean
  onIncludedChange: () => void
  onSourceChange: (source: string) => void
  onReset: () => void
  /** Kırpma modu kapandığında (kırpıldı ya da vazgeçildi) çağrılır; sohbet kompakt çubuğa döner. */
  onEditingEnd?: () => void
}

export function ScreenshotEditor({
  source,
  originalSource,
  included,
  isSubmitting,
  isCropped,
  onIncludedChange,
  onSourceChange,
  onReset,
  onEditingEnd,
}: ScreenshotEditorProps) {
  const imageRef = useRef<HTMLImageElement>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [firstPoint, setFirstPoint] = useState<CropPoint | null>(null)
  const [selection, setSelection] = useState<CropRectangle | null>(null)
  const [selectionError, setSelectionError] = useState(false)
  const [failure, setFailure] = useState(false)
  const [isApplying, setIsApplying] = useState(false)

  const beginEditing = () => {
    if (isSubmitting) return
    setFirstPoint(null)
    setSelection(null)
    setSelectionError(false)
    setFailure(false)
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setFirstPoint(null)
    setSelection(null)
    setIsEditing(false)
    onEditingEnd?.()
  }

  const selectPoint: React.MouseEventHandler<HTMLImageElement> = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    const point = {
      x: Math.max(0, Math.min(bounds.width, event.clientX - bounds.left)),
      y: Math.max(0, Math.min(bounds.height, event.clientY - bounds.top)),
    }
    if (!firstPoint || selection) {
      setFirstPoint(point)
      setSelection(null)
      setSelectionError(false)
      return
    }
    setSelection(normalizeCropRectangle(firstPoint, point))
  }

  const moveSelection: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (!selection || !['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return
    event.preventDefault()
    setSelection(moveCropRectangle(
      selection,
      event.key as 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight',
      event.shiftKey,
    ))
  }

  const applySelection = async () => {
    const image = imageRef.current
    if (!image || !selection || selection.width <= 0 || selection.height <= 0) {
      setSelectionError(true)
      return
    }
    const bounds = image.getBoundingClientRect()
    setIsApplying(true)
    const result = await applyCropImage({
      source,
      selection,
      dimensions: {
        displayWidth: bounds.width,
        displayHeight: bounds.height,
        naturalWidth: image.naturalWidth,
        naturalHeight: image.naturalHeight,
      },
    })
    setIsApplying(false)
    if ('reason' in result && result.reason === 'invalid-selection') {
      setSelectionError(true)
      return
    }
    setIsEditing(false)
    setFirstPoint(null)
    setSelection(null)
    if (result.ok) {
      setFailure(false)
      onSourceChange(result.dataUrl)
      onEditingEnd?.()
      return
    }
    onSourceChange(originalSource)
    setFailure(true)
  }

  return (
    <section className="rounded-2xl border border-border bg-muted/40 p-4">
      <p className="text-base font-semibold">Ekran görüntüsü</p>
      {isEditing ? (
        <div className="mt-3 space-y-3">
          <p id="crop-instructions" className="text-sm text-muted-foreground">
            Kırpmak istediğin alanın iki karşı köşesine dokun. Seçimi ok tuşlarıyla da taşıyabilirsin.
          </p>
          <div className="relative overflow-hidden rounded-xl border border-border bg-foreground/5">
            <img
              ref={imageRef}
              src={source}
              alt="Kırpılacak ekran görüntüsü"
              onClick={selectPoint}
              className="block w-full cursor-crosshair object-contain"
            />
            {selection ? (
              <div
                role="group"
                aria-label="Kırpma seçimi"
                aria-describedby="crop-instructions"
                tabIndex={0}
                onKeyDown={moveSelection}
                className="absolute border-2 border-brand-purple bg-brand-purple/10 shadow-[0_0_0_9999px_rgb(15_23_42/0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2"
                style={{ left: selection.x, top: selection.y, width: selection.width, height: selection.height }}
              >
                {(['-left-5 -top-5', '-right-5 -top-5', '-bottom-5 -left-5', '-bottom-5 -right-5'] as const).map((position) => (
                  <span key={position} aria-hidden="true" className={cn('absolute flex size-11 items-center justify-center', position)}>
                    <span className="size-3 rounded-full border-2 border-card bg-brand-purple" />
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          {!selection && firstPoint ? <p className="text-sm text-muted-foreground">Karşı köşeyi seç.</p> : null}
          {selectionError ? <p role="alert" className="text-sm text-destructive">Kırpmak için görünür bir alan seç.</p> : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={isApplying}
              onClick={() => void applySelection()}
              aria-busy={isApplying}
              className="min-h-11 rounded-full bg-brand-purple px-4 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {isApplying ? 'Kırpılıyor…' : 'Görüntüyü kırp'}
            </button>
            <button type="button" disabled={isApplying} onClick={cancelEditing} className="min-h-11 px-3 text-sm text-brand-purple underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2">
              Kırpmayı iptal et
            </button>
          </div>
        </div>
      ) : (
        <>
          <img src={source} alt="Ekran görüntüsü" className={cn('mt-3 w-full rounded-xl border border-border object-contain', !included && 'opacity-40')} />
          {failure ? (
            <div role="alert" className="mt-3 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <p>{CROP_FAILURE_COPY}</p>
              <div className="mt-2 flex flex-wrap gap-3">
                <button type="button" onClick={beginEditing} className="min-h-11 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2">Kırpmayı yeniden dene</button>
                <button type="button" onClick={() => setFailure(false)} className="min-h-11 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2">Kırpmadan devam et</button>
              </div>
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
            <button type="button" disabled={isSubmitting} onClick={onIncludedChange} aria-label="Ekran görüntüsünü dahil et veya çıkar" className="inline-flex min-h-11 items-center gap-2 text-sm text-brand-purple underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2">
              {included ? <ImageOff className="size-4" /> : <Camera className="size-4" />}
              {included ? 'Ekran görüntüsünü çıkar' : 'Geri ekle'}
            </button>
            <button type="button" disabled={isSubmitting} onClick={beginEditing} className="min-h-11 text-sm text-brand-purple underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2">Görüntüyü kırp</button>
            {isCropped ? <button type="button" disabled={isSubmitting} onClick={() => { setFailure(false); onReset() }} className="min-h-11 text-sm text-brand-purple underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2">Orijinale dön</button> : null}
          </div>
        </>
      )}
      <p className="mt-3 text-xs leading-relaxed text-warning-foreground">{SCREENSHOT_NOT_MASKED_COPY}</p>
    </section>
  )
}
